import { Box, Tooltip, Typography } from '@mui/material';

function scoreColor(score: number): string {
  return score >= 70 ? '#2e7d32' : score >= 40 ? '#ed6c02' : '#d32f2f';
}

/**
 * Inline per-item score-history sparkline (20×44 by default) + delta vs the
 * previous point (docs/features/retailer-catalog.md
 * §6.3). `history` is retention-pruned server-side (worker writes at
 * most one entry per manual-run day), so no client-side capping is needed.
 */
export function ScoreHistorySparkline({
  history,
  width = 44,
  height = 20,
}: {
  history?: Array<{ t: string; s: number }>;
  width?: number;
  height?: number;
}) {
  if (!history || history.length < 2) return null;
  const sorted = [...history].sort((a, b) => (a.t < b.t ? -1 : 1));
  const scores = sorted.map((e) => e.s);
  const pad = 2;
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const span = max - min || 1;
  const points = scores.map((v, i) => {
    const x = pad + (i / (scores.length - 1)) * (width - pad * 2);
    const y = height - pad - ((v - min) / span) * (height - pad * 2);
    return `${x},${y}`;
  });
  const last = scores[scores.length - 1];
  const prev = scores[scores.length - 2];
  const delta = last - prev;

  return (
    <Tooltip title={sorted.map((e) => `${e.t} : ${e.s}`).join(' · ')}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <polyline
            points={points.join(' ')}
            fill="none"
            stroke={scoreColor(last)}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {delta !== 0 && (
          <Typography variant="caption" sx={{ color: delta > 0 ? 'success.main' : 'error.main', fontSize: 11 }}>
            {delta > 0 ? '+' : ''}
            {delta}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
